import React from "react";
import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { 
  Business as CorporateIcon,
  People as FamilyIcon,
  Gavel as LegalIcon,
  Public as ImmigrationIcon,
  Home as RealEstateIcon,
  Security as DefenseIcon,
  Description as WillsEstatesIcon,
  Assignment as NotaryIcon,
  KeyboardArrowRight as ArrowIcon,
} from "@mui/icons-material";
import SeoMetaTags from "../components/SeoMetaTags";

// Styled components
const PageHeader = styled(Box)(({ theme }) => {
  // Get the theme URL dynamically
  const themeUrl = window.wpData?.themeUrl || '';
  
  return {
    backgroundImage: `linear-gradient(rgba(14, 49, 88, 0.85), rgba(14, 49, 88, 0.85)), url('${themeUrl}/build/grey-building.jpg')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    color: 'white',
    padding: theme.spacing(10, 0, 6, 0), // Increased top padding and adjusted bottom
    textAlign: 'center',
    marginBottom: theme.spacing(6),
    marginTop: theme.spacing(2), // Added top margin to account for navbar
  };
});

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2.5rem',
  marginBottom: theme.spacing(2),
}));



const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.75rem",
  marginBottom: theme.spacing(3),
  color: "#0e3158",
  position: "relative",
  paddingBottom: theme.spacing(1),
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "60px",
    height: "3px",
    backgroundColor: "#f0b94d",
  },
}));

const PracticeAreaSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  padding: theme.spacing(2, 0),
}));

const ServiceItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.5, 0),
}));

const ServiceIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: "30px",
  color: "#f0b94d",
}));

const CtaSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(8),
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: '#f5f7fa',
  borderRadius: '8px',
}));

function PracticeAreas() {
  return (
    <>
      {/* SEO Meta Tags */}
      <SeoMetaTags 
        slug="practice-areas" 
        defaultTitle="Practice Areas - Sentry Law | Calgary Legal Services"
        defaultDescription="Comprehensive legal services in Calgary including corporate law, litigation, real estate, personal injury, family law, immigration, and more. Contact us for expert legal assistance."
      />
      
      {/* Header Section */}
      <PageHeader>
        <Container maxWidth="lg">
          <PageTitle variant="h1">
            Practice Areas
          </PageTitle>
          <Typography variant="subtitle1" sx={{ maxWidth: 900, mx: 'auto', mb: 3, lineHeight: 1.7 }}>
            Our firm offers a broad spectrum of legal services. Each practice area is handled with the utmost competence and care, ensuring specialized attention to the unique aspects of your case.
          </Typography>
        </Container>
      </PageHeader>

      <Container maxWidth="lg" sx={{ mb: 8 }}>
        {/* Business and Corporate Law */}
        <PracticeAreaSection id="business-corporate-law">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CorporateIcon sx={{ fontSize: 40, color: '#0e3158', mr: 2 }} />
                <SectionTitle variant="h2">Corporate & Commercial Law</SectionTitle>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="body1" paragraph>
                At Sentry Law, we help businesses launch, grow, and succeed—while managing legal risk. We provide practical, full-service corporate and commercial legal support for entrepreneurs, startups, and established companies. From choosing the right structure to handling contracts and complex transactions, we focus on protecting your business so you can focus on results.
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#0e3158', fontWeight: 600 }}>
                Our Services:
              </Typography>
              
              <List disablePadding>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Business Formation & Structuring – Incorporations, partnerships, joint ventures, and shareholder/partnership agreements."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Contracts & Transactions – Drafting and reviewing sales agreements, service contracts, NDAs, mergers, acquisitions, and business sales."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Corporate Governance & Compliance – Legal advice for directors and officers, corporate record keeping, and regulatory compliance."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Commercial Legal Advice – Lease negotiations, financing support, and day-to-day business counsel."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
              </List>
              
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                Whether you're launching a new venture or scaling an existing one, we offer strategic legal guidance to support and protect your business at every stage.
              </Typography>
            </Grid>
          </Grid>
        </PracticeAreaSection>
        
        <Divider sx={{ my: 4 }} />
        
        {/* General Civil Litigation */}
        <PracticeAreaSection id="general-civil-litigation">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LegalIcon sx={{ fontSize: 40, color: '#0e3158', mr: 2 }} />
                <SectionTitle variant="h2">General Civil Litigation</SectionTitle>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="body1" paragraph>
                We focus on resolving your civil dispute efficiently and cost-effectively—often before formal litigation begins. Our team is skilled in negotiation and dedicated to achieving a successful outcome without the need for court, saving you both time and expense.
              </Typography>
              
              <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                Our areas of expertise include Personal injury Law and Employment Law
              </Typography>
              
              {/* Personal Injury Law Sub-section */}
              <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, color: '#0e3158', fontWeight: 600 }}>
                Personal Injury
              </Typography>
              <Typography variant="body2" paragraph>
                If you've been injured due to someone else's negligence, Sentry Law is here to fight for your right to fair compensation. We combine strong legal advocacy with compassionate support—so you can focus on recovery while we handle the rest.
              </Typography>
              <Typography variant="body2" paragraph sx={{ fontWeight: 600 }}>
                We work on a contingency fee basis: no legal fees unless you win.
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: '#0e3158', fontWeight: 600, fontSize: '1rem' }}>
                Our Services:
              </Typography>
              
              <List disablePadding sx={{ ml: 2 }}>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Motor Vehicle Accidents – Representing drivers, passengers, cyclists, and pedestrians injured in crashes. We manage insurance claims and pursue damages for medical costs, lost income, pain and suffering, and more."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Slip & Fall / Occupiers' Liability – Helping victims injured on unsafe property recover compensation under Alberta's Occupiers' Liability Act."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Catastrophic Injury & Wrongful Death – Advocating for clients with life-altering injuries or families who've lost a loved one, including complex claims for long-term care, income loss, and future needs."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Insurance Disputes – Challenging denied or delayed disability and accident benefits to secure the coverage you deserve."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
              </List>
              
              <Typography variant="body2" paragraph sx={{ mt: 2 }}>
                From consultation to resolution, we're in your corner—relieving your legal burden and fighting for full, fair compensation.
              </Typography>
              
              {/* Employment Law Sub-section */}
              <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, color: '#0e3158', fontWeight: 600 }}>
                Employment Law
              </Typography>
              <Typography variant="body2" paragraph>
                Sentry Law advises both employees and employers on workplace rights, responsibilities, and risk management. We offer clear, practical guidance on contracts, dismissals, and workplace disputes—always with a focus on fairness, compliance, and cost-effective solutions.
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: '#0e3158', fontWeight: 600, fontSize: '1rem' }}>
                Our Services:
              </Typography>
              
              <List disablePadding sx={{ ml: 2 }}>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Employment Contracts – Drafting and reviewing employment and contractor agreements, including clear terms on duties, compensation, termination, and restrictive covenants."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Workplace Policies – Helping employers create compliant policies (e.g., harassment, privacy, vacation) and advising employees on their rights under changing workplace rules."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Dismissal & Severance – Representing employees in wrongful dismissal and severance disputes; guiding employers through lawful termination to reduce liability."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Workplace Disputes & Human Rights – Handling claims of harassment, discrimination, and constructive dismissal before courts, tribunals, or the Alberta Human Rights Commission."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
              </List>
              
              <Typography variant="body2" paragraph sx={{ mt: 2 }}>
                Whether you're managing staff or facing termination, we help ensure your workplace rights and obligations are protected.
              </Typography>
            </Grid>
          </Grid>
        </PracticeAreaSection>
        
        <Divider sx={{ my: 4 }} />
        
        {/* Wills & Estates */}
        <PracticeAreaSection id="wills-estates">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WillsEstatesIcon sx={{ fontSize: 40, color: '#0e3158', mr: 2 }} />
                <SectionTitle variant="h2">Wills & Estates</SectionTitle>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="body1" paragraph>
                Planning ahead is one of the most meaningful ways to support your loved ones. At Sentry Law, we offer clear, compassionate guidance for estate planning, probate, and elder law matters—ensuring your wishes are honored and your family is supported.
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#0e3158', fontWeight: 600 }}>
                Our Services:
              </Typography>
              
              <List disablePadding>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Estate Planning – Custom wills, family and minors' trusts, powers of attorney, and personal directives for a complete and secure estate plan."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Probate & Estate Administration – Helping executors manage assets, pay debts, and distribute inheritances through Alberta's probate process."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Inheritance & Estate Disputes – Advising on executor duties, beneficiary rights, and resolving conflicts through negotiation or litigation."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Elder Law & Incapacity Planning – Assisting with guardianship, trusteeship, and asset protection strategies in cases of illness or incapacity."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
              </List>
              
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                We help you protect your legacy with clarity and care—so your loved ones can move forward with confidence and peace of mind.
              </Typography>
            </Grid>
          </Grid>
        </PracticeAreaSection>
        
        <Divider sx={{ my: 4 }} />
        
        {/* Real Estate */}
        <PracticeAreaSection id="real-estate">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <RealEstateIcon sx={{ fontSize: 40, color: '#0e3158', mr: 2 }} />
                <SectionTitle variant="h2">Real Estate Law (Residential & Commercial)</SectionTitle>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="body1" paragraph>
                Sentry Law provides reliable legal support for residential and commercial real estate transactions. Whether you're buying, selling, leasing, or refinancing, we ensure your deal closes smoothly and your investment is protected. We handle the details, coordinate with third parties, and safeguard your interests from contract to closing.
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#0e3158', fontWeight: 600 }}>
                Our Services:
              </Typography>
              
              <List disablePadding>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Residential Transactions – Assisting with home purchases and sales, including contract review, title searches, condition removals, and closing documentation."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Commercial Real Estate & Leasing – Supporting businesses and investors with property acquisitions, sales, and lease agreements—clearly defining key terms to avoid future disputes."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Refinancing & Mortgages – Managing mortgage registrations, reviewing financing documents, and ensuring timely payouts to meet lender deadlines."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Real Estate Disputes – Resolving contract breaches, deposit issues, or property defects through negotiation or litigation when necessary."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
              </List>
              
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                With Sentry Law by your side, real estate transactions become streamlined, secure, and stress-free.
              </Typography>
            </Grid>
          </Grid>
        </PracticeAreaSection>
        
        <Divider sx={{ my: 4 }} />
        
        {/* Family Law */}
        <PracticeAreaSection id="family-law">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FamilyIcon sx={{ fontSize: 40, color: '#0e3158', mr: 2 }} />
                <SectionTitle variant="h2">Family Law</SectionTitle>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="body1" paragraph>
                At Sentry Law, we provide compassionate, strategic guidance to help you navigate life's most difficult transitions. Our goal is to protect your rights and your family's well-being through personalized, practical solutions. We listen carefully, understand your unique circumstances, and advocate for outcomes that support your future with confidence and security.
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#0e3158', fontWeight: 600 }}>
                Our Services:
              </Typography>
              
              <List disablePadding>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Separation & Divorce – Guidance on contested and uncontested divorces, legal separations, division of property, debt, and the matrimonial home."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Child Custody & Parenting – Support with parenting plans, custody, guardianship, and access arrangements—always with the child's best interests in mind."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Child & Spousal Support – Advice and representation on support entitlements and obligations under Alberta guidelines."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Agreements & Mediation – Drafting prenuptial, postnuptial, and cohabitation agreements; resolving disputes through negotiation or mediation when possible."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
              </List>
              
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                We aim to reduce conflict and reach fair, lasting resolutions. When cooperation is possible, we are creative problem-solvers. When litigation is necessary, we are strong, tenacious advocates for your rights and your family's future.
              </Typography>
            </Grid>
          </Grid>
        </PracticeAreaSection>
        
        <Divider sx={{ my: 4 }} />
        
        {/* Criminal Defence Law */}
        <PracticeAreaSection id="criminal-defence-law">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DefenseIcon sx={{ fontSize: 40, color: '#0e3158', mr: 2 }} />
                <SectionTitle variant="h2">Criminal Defence</SectionTitle>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="body1" paragraph>
                Being charged with a crime can be overwhelming. At Sentry Law, we provide strong, strategic defence to protect your rights, reputation, and freedom. From minor offences to serious charges, we guide you through every stage of the process with clarity and commitment.
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#0e3158', fontWeight: 600 }}>
                Our Services:
              </Typography>
              
              <List disablePadding>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Impaired Driving (DUI) – Defending charges for impaired driving, over 0.08, or refusal. We assess police conduct and testing procedures to fight for dismissals, Charter defences, or reduced penalties."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="White-Collar Crime – Representing clients accused of fraud, embezzlement, or financial offences. We analyze complex evidence and work with experts to build a strong defence or negotiate favourable outcomes."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Youth Criminal Defence – Advocating for young persons under the Youth Criminal Justice Act, prioritizing rehabilitation through diversion or alternative measures."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="General Criminal Charges – Handling matters such as assault, theft, mischief, and drug offences. We fight for bail, explore defences, and represent you in plea or trial."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
              </List>
              
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                You don't have to face the justice system alone. Sentry Law investigates thoroughly, challenges the prosecution, and works to achieve the best possible result—so you can move forward with confidence.
              </Typography>
            </Grid>
          </Grid>
        </PracticeAreaSection>
        
        <Divider sx={{ my: 4 }} />
        
        {/* Immigration Law */}
        <PracticeAreaSection id="immigration-law">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ImmigrationIcon sx={{ fontSize: 40, color: '#0e3158', mr: 2 }} />
                <SectionTitle variant="h2">Immigration Law</SectionTitle>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="body1" paragraph>
                At Sentry Law, we help individuals and families navigate the Canadian immigration system with confidence. Whether you're applying to live, work, study, or reunite with loved ones in Canada, we offer clear, up-to-date guidance and strong support at every stage.
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#0e3158', fontWeight: 600 }}>
                Our Services:
              </Typography>
              
              <List disablePadding>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Permanent Residence – Assistance with Express Entry, Provincial Nominee Program (PNP), and family sponsorships for spouses, children, and parents."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Temporary Residence & Visas – Work permits (including LMIA), study permits, visitor visas, and extensions."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Citizenship & PR Cards – Citizenship applications, test preparation, PR card renewals, and advice on maintaining status."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Immigration Appeals & Challenges – Representation in hearings, sponsorship appeals, judicial reviews, and inadmissibility cases."
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
              </List>
              
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                Immigration is a major step—let us manage the legal process so you can focus on your future in Canada.
              </Typography>
            </Grid>
          </Grid>
        </PracticeAreaSection>
        
        <Divider sx={{ my: 4 }} />
        
        {/* Notary and Commissioner for Oaths */}
        <PracticeAreaSection id="notary-commissioner">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <NotaryIcon sx={{ fontSize: 40, color: '#0e3158', mr: 2 }} />
                <SectionTitle variant="h2">Notary & Commissioner for Oaths</SectionTitle>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="body1" paragraph>
                Sentry Law offers quick, reliable notary and commissioner services for individuals and businesses. Whether you need a document notarized, an affidavit sworn, or a statutory declaration commissioned, we ensure your paperwork is completed properly and promptly.
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#0e3158', fontWeight: 600 }}>
                Our Services:
              </Typography>
              
              <List disablePadding>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Notarization of documents and certified true copies"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Swearing affidavits and statutory declarations"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Administering oaths for legal, immigration, or court documents"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
                <ServiceItem>
                  <ServiceIcon>
                    <ArrowIcon fontSize="small" />
                  </ServiceIcon>
                  <ListItemText 
                    primary="Witnessing signatures on legal forms and agreements"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ServiceItem>
              </List>
              
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                Appointments are available on short notice. We make the process smooth and efficient—so you can get on with what matters.
              </Typography>
            </Grid>
          </Grid>
        </PracticeAreaSection>
        
        <Divider sx={{ my: 4 }} />
        
        {/* CTA Section */}
        <CtaSection>
          <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 700, color: '#0e3158' }}>
            Get Expert Legal Advice
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
            Our experienced attorneys are ready to help you navigate your legal challenges with personalized
            solutions tailored to your specific needs.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              component={RouterLink}
              to="/contact-us"
              sx={{ 
                bgcolor: '#f0b94d', 
                color: '#0e3158', 
                fontWeight: 600,
                '&:hover': { bgcolor: '#d9a843' }, 
                px: 3, 
                py: 1.5 
              }}
            >
              Schedule Free Consultation
            </Button>
          </Box>
        </CtaSection>
      </Container>
    </>
  );
}

export default PracticeAreas;